import { useMutation } from '@platform/api-client';
import type { Operation, RequestBody, TypedClient } from '@platform/openapi';
import { z } from 'zod';
import { nullWhenEmpty, optionalTextField } from '../optional-text.ts';
import { usePageContext } from '../usePageContext.ts';
import type { Location } from './inventory.ts';

export type DepotBody = RequestBody<Operation<'/admin/locations', 'post'>>;

export const DEPOT_LIMITS = { code: 40, name: 200, phone: 30, address: 300, city: 100 } as const;

export const depotFormSchema = z.object({
  name: z.string().trim().min(1).max(DEPOT_LIMITS.name),
  code: z.string().trim().min(1).max(DEPOT_LIMITS.code),
  phone: optionalTextField(DEPOT_LIMITS.phone),
  address: optionalTextField(DEPOT_LIMITS.address),
  governorate: z.string(),
  city: optionalTextField(DEPOT_LIMITS.city),
  isDefault: z.boolean(),
});

export type DepotFormValues = z.infer<typeof depotFormSchema>;

export const depotBodyOf = (values: DepotFormValues): DepotBody => ({
  name: values.name,
  code: values.code.toUpperCase(),
  phone: nullWhenEmpty(values.phone),
  address: nullWhenEmpty(values.address),
  city: nullWhenEmpty(values.city),
  isDefault: values.isDefault,
});

export const depotFormOf = (depot: Location | null): DepotFormValues => ({
  name: depot?.name ?? '',
  code: depot?.code ?? '',
  phone: depot?.phone ?? '',
  address: depot?.address ?? '',
  governorate: '',
  city: depot?.city ?? '',
  isDefault: depot?.isDefault ?? false,
});

export const depotPlaceOf = (depot: Location): string =>
  [depot.address, depot.city].filter((part) => part !== null).join(', ');

export type DepotChange =
  | { readonly kind: 'save'; readonly depot: Location | null; readonly body: DepotBody }
  | { readonly kind: 'archive'; readonly depot: Location };

const messageOf = (change: DepotChange): string => {
  if (change.kind === 'archive') return 'depots.archived';
  return change.depot === null ? 'depots.created' : 'depots.saved';
};

export const useDepotChanges = (api: TypedClient) => {
  const { t, notify, invalidate } = usePageContext();
  const send = (change: DepotChange): Promise<Location> => {
    if (change.kind === 'archive')
      return api.post('/admin/locations/{id}/archive', {
        path: { id: change.depot.id },
        body: { version: change.depot.version },
      });
    if (change.depot === null) return api.post('/admin/locations', { body: change.body });
    return api.patch('/admin/locations/{id}', {
      path: { id: change.depot.id },
      body: { ...change.body, version: change.depot.version },
    });
  };
  return useMutation({
    mutationFn: send,
    onSuccess: async (depot, change) => {
      notify(t(messageOf(change), { name: depot.name }));
      await invalidate('locations', 'dashboard');
    },
  });
};
